// masterclass — c06 Murabbiy. A single-instructor masterclass card.
//
// Design language: split bookplate.
//   • Left half (charcoal-toned): a single big portrait of the instructor
//     bleeds to the edge. A warm dark overlay tints the photo so the page
//     reads as one quiet, classical study — bookish, restrained.
//   • Right half (stone): editorial typography stacked vertically with a
//     "MASTERCLASS" small-caps eyebrow at the top, the topic title set
//     large in Marcellus (italic), the instructor's name italic underneath
//     in a smaller cut, and the date + venue micro line at the foot.
//   • A subtle gold thread (a 1-px hairline at ~50% accent opacity)
//     divides the two halves vertically. The thread sprouts a single
//     hairline diamond at the midpoint so the divide reads as a
//     bookbinder's ribbon, not a brutal slash.
//   • Four discreet decorative corners on the stone half ground the type.

import { Photo, type LayoutCtx } from "./_shared";
import * as O from "@/components/Ornaments";

let cardSeq = 0;
const nextCardId = () => `murabbiy-card-${++cardSeq}`;

// Gold thread accent — a soft sandy-warm gold that sits between the
// stone and the warm dark. We mix a fixed gold with the accent so the
// thread reads consistently regardless of the chosen accent token.
const GOLD = "#B8975A";

export function MasterclassLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
  photos,
}: LayoutCtx) {
  const id = nextCardId();
  const heroSrc = photo || photos[0];

  // The names string in data.ts is "MASTERCLASS · ART OF UI". We split it
  // so the eyebrow and the title can each occupy their own typographic
  // register — eyebrow tracked-out small caps, title large italic.
  const parts = names.split("·").map((s) => s.trim());
  const eyebrow = parts[0] || "MASTERCLASS";
  const title = parts.slice(1).join(" · ") || names;

  // Fictional senior instructor — Erkin Yusupov, 15 years in product /
  // UI design. Hardcoded here on purpose: the layout is a fixed frame
  // for this template; data.ts only carries the topic in `names`.
  const instructor = "Erkin Yusupov";

  return (
    <div
      className={`${id}-card`}
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        background: bg,
        color: ink,
      }}
    >
      <style>{`
        .${id}-card { font-feature-settings: "smcp" 1, "lnum" 1; }
      `}</style>

      {/* ── Background paper texture (very subtle warm noise) ─────── */}
      <svg
        viewBox="0 0 200 280"
        preserveAspectRatio="none"
        aria-hidden
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
      >
        <defs>
          <filter id={`${id}-paper`} x="0" y="0" width="100%" height="100%">
            <feTurbulence type="fractalNoise" baseFrequency="1.4" numOctaves="2" seed="7" />
            <feColorMatrix type="matrix" values="0 0 0 0 0.85  0 0 0 0 0.78  0 0 0 0 0.66  0 0 0 0.05 0" />
          </filter>
        </defs>
        <rect width="200" height="280" fill={bg} />
        <rect width="200" height="280" filter={`url(#${id}-paper)`} opacity="0.5" />
      </svg>

      {/* ── LEFT HALF — instructor portrait with charcoal tint ─────── */}
      <div
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          width: "50%",
          overflow: "hidden",
        }}
      >
        <Photo
          src={heroSrc}
          fallback={
            <div
              style={{
                width: "100%",
                height: "100%",
                background: `linear-gradient(160deg, ${ink}55, ${ink}88)`,
                display: "grid",
                placeItems: "center",
              }}
            >
              <svg width="44%" viewBox="0 0 60 70" aria-hidden>
                <circle cx="30" cy="22" r="12" fill={bg} opacity="0.7" />
                <path d="M8 70 Q 8 40 30 40 Q 52 40 52 70 Z" fill={bg} opacity="0.7" />
              </svg>
            </div>
          }
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
        />
        {/* Charcoal/warm-dark wash so the portrait reads as a study,
            not a snapshot. Top→bottom darkening helps the type below. */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(180deg, ${ink}55 0%, ${ink}88 100%)`,
            mixBlendMode: "multiply",
          }}
        />
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(140% 100% at 30% 30%, transparent 40%, ${ink}55 100%)`,
          }}
        />

        {/* Instructor caption that "etches" itself onto the photo at
            the bottom-left, in stone over the dark wash. */}
        <div
          style={{
            position: "absolute",
            left: "8%",
            right: "8%",
            bottom: "6%",
            color: bg,
          }}
        >
          <div
            style={{
              fontFamily: "var(--font-ui)",
              fontSize: 5,
              fontWeight: 700,
              letterSpacing: "0.4em",
              opacity: 0.85,
              marginBottom: 4,
            }}
          >
            INSTRUCTOR
          </div>
          <div
            style={{
              fontFamily: font,
              fontStyle: "italic",
              fontSize: 9,
              fontWeight: 400,
              letterSpacing: "0.04em",
              lineHeight: 1.1,
            }}
          >
            {instructor}
          </div>
          <div style={{ width: 18, height: 0.6, background: GOLD, marginTop: 5, opacity: 0.85 }} />
        </div>
      </div>

      {/* ── GOLD THREAD — vertical hairline + midpoint diamond ───── */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          left: "50%",
          top: "8%",
          bottom: "8%",
          width: 0.6,
          background: GOLD,
          opacity: 0.7,
          transform: "translateX(-50%)",
        }}
      />
      <div
        aria-hidden
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%) rotate(45deg)",
          width: 5,
          height: 5,
          background: bg,
          border: `0.4px solid ${GOLD}`,
        }}
      />

      {/* ── RIGHT HALF — stone, editorial typography ──────────────── */}
      <div
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          right: 0,
          width: "50%",
          padding: "12% 8% 10% 9%",
          display: "flex",
          flexDirection: "column",
          color: ink,
        }}
      >
        {/* Decorative corners on the stone half so the right page reads
            as a bound title page. */}
        <div
          style={{ position: "absolute", top: 4, right: 4, color: ink, opacity: 0.6, transform: "scaleX(-1)" }}
          aria-hidden
        >
          <O.DecoCorner size={26} color={ink} />
        </div>
        <div
          style={{ position: "absolute", bottom: 4, right: 4, color: ink, opacity: 0.6, transform: "scale(-1, -1)" }}
          aria-hidden
        >
          <O.DecoCorner size={26} color={ink} />
        </div>

        {/* Eyebrow — MASTERCLASS, tracked out, in gold */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 700,
            letterSpacing: "0.46em",
            color: GOLD,
          }}
        >
          {eyebrow}
        </div>
        <div style={{ width: 14, height: 0.6, background: GOLD, marginTop: 5, opacity: 0.9 }} />

        {/* Topic title in Marcellus — large, italic, balanced lines */}
        <div
          style={{
            fontFamily: font,
            fontStyle: "italic",
            fontSize: 18,
            fontWeight: 400,
            lineHeight: 1.05,
            letterSpacing: "0.01em",
            color: ink,
            marginTop: 14,
          }}
        >
          {title}
        </div>

        {/* Hairline rule between title and instructor */}
        <div style={{ width: "60%", height: 0.5, background: ink, opacity: 0.35, margin: "10px 0 8px" }} />

        {/* Instructor name — italic, accent-toned */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 5.5,
            fontWeight: 700,
            letterSpacing: "0.36em",
            color: accent,
            opacity: 0.85,
          }}
        >
          MURABBIY
        </div>
        <div
          style={{
            fontFamily: font,
            fontStyle: "italic",
            fontSize: 11,
            fontWeight: 400,
            letterSpacing: "0.02em",
            color: ink,
            marginTop: 4,
          }}
        >
          {instructor}
        </div>
        <div
          style={{
            fontFamily: "var(--font-body)",
            fontStyle: "italic",
            fontSize: 5.5,
            color: `${ink}aa`,
            marginTop: 3,
            lineHeight: 1.3,
          }}
        >
          15 yillik tajriba · senior product designer
        </div>

        {/* Spacer to push the date/venue to the bottom of the page. */}
        <div style={{ flex: 1 }} />

        {/* Foot — date + venue with gold flank lines */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginTop: 8,
          }}
        >
          <div style={{ flex: 1, height: 0.6, background: GOLD, opacity: 0.7 }} />
          <O.Diamond size={5} color={GOLD} />
          <div style={{ flex: 1, height: 0.6, background: GOLD, opacity: 0.7 }} />
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 5.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: ink,
            marginTop: 8,
            textAlign: "center",
          }}
        >
          {date}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 5,
            fontWeight: 600,
            letterSpacing: "0.36em",
            color: `${ink}aa`,
            marginTop: 3,
            textAlign: "center",
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
